import apiRequest from "./axiosInstance";

/**
 * Fetches a list of articles with pagination
 * @param {Object} [data] - Query parameters for fetching articles
 * @param {number} [data.limit] - Number of articles to fetch per page
 * @param {number} [data.page] - Page number for pagination
 * @returns {Promise<Object>} Response object from the API request
 */
export const fetchArticles = (data) =>
  apiRequest("get", `/articles?limit=${data?.limit}&page=${data?.page}`);

export const FilterOptions = () => apiRequest("get", `/articles/allFilters`);

/**
 * Fetches an article by its ID
 * @param {string|number} id - Article ID
 * @returns {Promise<Object>} Response object from the API request
 */
export const fetchArticleById = (id) => apiRequest("get", `/articles/${id}`);

export const fetchVolIss = (data) => {
  const queryParts = [];

  if (data.id) queryParts.push(`id=${encodeURIComponent(data.id)}`);
  if (data.type) queryParts.push(`type=${encodeURIComponent(data.type)}`);
  if (data.time) queryParts.push(`time=${encodeURIComponent(data.time)}`);
  if (data.journal)
    queryParts.push(`journal=${encodeURIComponent(data.journal)}`);
  if (data.place) queryParts.push(`place=${encodeURIComponent(data.place)}`);

  const queryString = queryParts.filter(Boolean).join("&");

  return apiRequest("post", `/volIss?${queryString}`);
};

export const fetchArticleByUserId = (id, data) => {
  const journalQuery = Array.isArray(data.journal)
    ? data.journal
        .filter((dt) => dt && dt.id !== "all")
        .map((dt) => `journal=${dt.id}`)
        .join("&")
    : "";

  const docHeadQuery = data.docHead?.includes("All")
    ? ""
    : data.docHead
        ?.map((type) => `docHead=${encodeURIComponent(type)}`)
        .join("&") || "";

  const dueDateQuery =
    data.dueDate
      ?.map((date) => `dueDate=${encodeURIComponent(date)}`)
      .join("&") || "";

  const copyeditQuery = data.copyedit?.includes("All")
    ? ""
    : data.copyedit
        ?.map((ce) => `copyedit=${encodeURIComponent(ce)}`)
        .join("&") || "";

  const volIssQuery =
    data.volIss
      ?.map((vi) => {
        const [volume, issue] = vi.split("/");

        if (
          volume &&
          issue &&
          volume !== "All" &&
          issue !== "All" &&
          volume !== "undefined" &&
          issue !== "undefined"
        ) {
          return `volume=${encodeURIComponent(
            volume
          )}&issue=${encodeURIComponent(issue)}`;
        }

        return null;
      })
      .filter(Boolean)
      .join("&") || "";

  const searchQuery = data.search
    ? `search=${encodeURIComponent(data.search)}`
    : "";

  const queryString = [
    `limit=${data.limit}`,
    `page=${data.page}`,
    `time=${data.time}`,
    `key=${data.key}`,
    `direction=${data.direction}`,
    journalQuery,
    docHeadQuery,
    dueDateQuery,
    copyeditQuery,
    volIssQuery,
    searchQuery,
  ]
    .filter(Boolean)
    .join("&");

  return apiRequest("post", `/articles/user/${id}?${queryString}`);
};

export const fetchArticleByUnAssigned = (data) => {
  const journalQuery = Array.isArray(data.journal)
    ? data.journal
        .filter((dt) => dt && dt.id !== "all")
        .map((dt) => `journal=${dt.id}`)
        .join("&")
    : "";

  const docHeadQuery = data.docHead?.includes("All")
    ? ""
    : data.docHead
        ?.map((type) => `docHead=${encodeURIComponent(type)}`)
        .join("&") || "";

  const copyeditQuery = data.copyedit?.includes("All")
    ? ""
    : data.copyedit
        ?.map((ce) => `copyedit=${encodeURIComponent(ce)}`)
        .join("&") || "";

  const dueDateQuery =
    data.dueDate
      ?.map((date) => `dueDate=${encodeURIComponent(date)}`)
      .join("&") || "";

  const pointQuery = data.points?.includes("All")
    ? ""
    : data.points?.map((ce) => `points=${encodeURIComponent(ce)}`).join("&") ||
      "";

  const searchQuery = data.search
    ? `search=${encodeURIComponent(data.search)}`
    : "";

  const volIssQuery =
    data.volIss
      ?.map((vi) => {
        const [volume, issue] = vi.split("/");

        if (
          volume &&
          issue &&
          volume !== "All" &&
          issue !== "All" &&
          volume !== "undefined" &&
          issue !== "undefined"
        ) {
          return `volume=${encodeURIComponent(
            volume
          )}&issue=${encodeURIComponent(issue)}`;
        }

        return null;
      })
      .filter(Boolean)
      .join("&") || "";

  const preAcceptQuery =
    typeof data.preAccept === "boolean" ? `preAccept=${data.preAccept}` : "";

  const sceReturnedStatusQuery =
    typeof data.sceReturnedStatus === "boolean"
      ? `sceReturnedStatus=${data.sceReturnedStatus}`
      : "";

  const itemHoldoutQuery =
    typeof data.itemHoldout === "boolean"
      ? `itemHoldout=${data.itemHoldout}`
      : "";

  const queryString = [
    `limit=${data.limit}`,
    `page=${data.page}`,
    `key=${data.key}`,
    `direction=${data.direction}`,
    `userId=${data.userId}`,
    journalQuery,
    docHeadQuery,
    dueDateQuery,
    copyeditQuery,
    searchQuery,
    volIssQuery,
    pointQuery,
    preAcceptQuery,
    sceReturnedStatusQuery,
    itemHoldoutQuery,
  ]
    .filter(Boolean)
    .join("&");

  return apiRequest("post", `/articles/unassign?${queryString}`);
};

export const fetchArticleByJournal = (name, data) => {
  const docHeadQuery = data.docHead?.includes("All")
    ? ""
    : data.docHead
        ?.map((ce) => `docHead=${encodeURIComponent(ce)}`)
        .join("&") || "";

  const pitQuery = data.pit?.includes("All")
    ? ""
    : data.pit?.map((ce) => `pit=${encodeURIComponent(ce)}`).join("&") || "";

  const copyeditQuery = data.copyedit?.includes("All")
    ? ""
    : data.copyedit
        ?.map((ce) => `copyedit=${encodeURIComponent(ce)}`)
        .join("&") || "";

  const onDateQuery =
    data.onDate &&
    data.onDate.from &&
    data.onDate.to &&
    data.onDate.from.trim() !== "" &&
    data.onDate.to.trim() !== ""
      ? `onDateFrom=${encodeURIComponent(
          data.onDate.from
        )}&onDateTo=${encodeURIComponent(data.onDate.to)}`
      : "";

  const productionHandlerQuery =
    data.productionHandler
      ?.filter((production) => production !== undefined && production !== "All")
      .map(
        (production) => `productionHandler=${encodeURIComponent(production)}`
      )
      .join("&") || "";

  const searchQuery = data.search
    ? `search=${encodeURIComponent(data.search)}`
    : "";

  const volIssQuery =
    data.volIss
      ?.map((vi) => {
        const [volume, issue] = vi.split("/");

        if (
          volume &&
          issue &&
          volume !== "All" &&
          issue !== "All" &&
          volume !== "undefined" &&
          issue !== "undefined"
        ) {
          return `volume=${encodeURIComponent(
            volume
          )}&issue=${encodeURIComponent(issue)}`;
        }

        return null;
      })
      .filter(Boolean)
      .join("&") || "";

  const sceReturnedStatusQuery =
    typeof data.sceReturned === "boolean"
      ? `sceReturned=${data.sceReturned}`
      : "";

  const pressReleaseQuery =
    typeof data.pressRelease === "boolean"
      ? `pressRelease=${data.pressRelease}`
      : "";

  const articleS300DateQuery =
    typeof data.articleS300Date === "boolean"
      ? `articleS300Date=${data.articleS300Date}`
      : "";
  const copyEditTaskCompleteQuery =
    typeof data.copyEditTaskComplete === "boolean"
      ? `copyEditTaskComplete=${data.copyEditTaskComplete}`
      : "";
  const cfcTaskCompleteQuery =
    typeof data.cfcTaskComplete === "boolean"
      ? `cfcTaskComplete=${data.cfcTaskComplete}`
      : "";
  const coverSubReceivedQuery =
    typeof data.coverSubReceived === "boolean"
      ? `coverSubReceived=${data.coverSubReceived}`
      : "";
  const preAcceptDateQuery =
    typeof data.preAcceptDate === "boolean"
      ? `preAcceptDate=${data.preAcceptDate}`
      : "";
  const sdPublishedOnWebS300Query =
    typeof data.sdPublishedOnWebS300 === "boolean"
      ? `sdPublishedOnWebS300=${data.sdPublishedOnWebS300}`
      : "";

      

  const queryString = [
    `limit=${data.limit}`,
    `page=${data.page}`,
    `key=${data.key}`,
    `direction=${data.direction}`,
    `userId=${data.id}`,
    docHeadQuery,
    productionHandlerQuery,
    copyeditQuery,
    searchQuery,
    volIssQuery,
    pitQuery,
    sceReturnedStatusQuery,
    pressReleaseQuery,
    articleS300DateQuery,
    copyEditTaskCompleteQuery,
    cfcTaskCompleteQuery,
    coverSubReceivedQuery,
    preAcceptDateQuery,
    sdPublishedOnWebS300Query,
    onDateQuery,
  ]
    .filter(Boolean)
    .join("&");

  return apiRequest("post", `/articles/journal/${name}?${queryString}`);
};

export const fetchArticleByIssue = (data) =>
  apiRequest(
    "get",
    `/articles/issue/${data.id}?key=${data.key}&direction=${data.direction}`
  );

/**
 * Creates a new article
 * @param {Object} articleData - Data for the new article
 * @returns {Promise<Object>} Response object from the API request
 */
export const addArticle = (articleData) =>
  apiRequest("post", "/articles", articleData, {}, {}, true);

/**
 * Updates an existing article
 * @param {string|number} id - Article ID
 * @param {Object} articleData - Updated article data
 * @returns {Promise<Object>} Response object from the API request
 */
export const updateArticle = (id, articleData) =>
  apiRequest("put", `/articles/user/${id}`, articleData);

export const updateTaskArticle = (id, articleData) =>
  apiRequest("put", `/articles/taskAssignment/${id}`, articleData);

export const updateJournalArticle = (id, articleData) =>
  apiRequest("put", `/articles/journalOverview/${id}`, articleData);

/**
 * Deletes an article by its ID
 * @param {string|number} id - Article ID
 * @returns {Promise<Object>} Response object from the API request
 */
export const deleteArticle = (id) => apiRequest("delete", `/articles/${id}`);

/**
 * Article API service module
 * @module
 */
